import { ref, onUnmounted } from 'vue';
import { driver } from 'driver.js';
import type { Driver } from 'driver.js';
import { useInteractiveGuidesStore } from 'src/stores/interactive-guides-store';
import { DRIVER_DEFAULTS } from 'src/boot/interactive-guides';
import type {
  UseInteractiveGuideOptions,
  UseInteractiveGuideReturn,
  InteractiveGuideResponse,
  InteractiveGuideStepResponse,
  GuideStep,
} from 'src/types/interactive-guides';
import { getCompletionKey } from 'src/types/interactive-guides';

const BADGE_CLASS = 'interactive-guide-badge';
const DEFAULT_HOVER_DELAY = 6000;

interface HoverState {
  timer: ReturnType<typeof setTimeout> | null;
  badge: HTMLElement | null;
  listeners: {
    mouseenter: () => void;
    mouseleave: () => void;
  };
}

function createBadgeElement(onClick: () => void): HTMLElement {
  const badge = document.createElement('div');
  badge.className = BADGE_CLASS;
  badge.innerHTML = '?';
  badge.setAttribute('role', 'button');
  badge.setAttribute('aria-label', 'Show help for this section');

  Object.assign(badge.style, {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: 'rgba(25, 118, 210, 0.5)',
    color: 'white',
    fontSize: '10px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: '1000',
    transition: 'opacity 0.2s, background-color 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  });

  badge.addEventListener('mouseenter', () => {
    badge.style.backgroundColor = 'rgba(25, 118, 210, 0.8)';
  });

  badge.addEventListener('mouseleave', () => {
    badge.style.backgroundColor = 'rgba(25, 118, 210, 0.5)';
  });

  badge.addEventListener('click', (e) => {
    e.stopPropagation();
    onClick();
  });

  return badge;
}

/**
 * Composable for managing interactive guide tours.
 *
 * @example
 * ```typescript
 * const { startTour, isLoading, hasCompleted } = useInteractiveGuide({
 *   slug: 'member-profile-tour',
 *   autoStart: true,
 *   onComplete: () => console.log('Tour completed!'),
 * });
 * ```
 */
export function useInteractiveGuide(
  options: UseInteractiveGuideOptions
): UseInteractiveGuideReturn {
  const store = useInteractiveGuidesStore();

  // Reactive state
  const isLoading = ref(false);
  const isActive = ref(false);
  const guide = ref<InteractiveGuideResponse | null>(null);
  const error = ref<Error | null>(null);

  // Driver.js instance
  let driverInstance: Driver | null = null;

  // Hover badge tracking
  const hoverStates = new Map<string, HoverState>();
  const hoverDelay = options.hoverBadgeDelay ?? DEFAULT_HOVER_DELAY;

  // Completion tracking
  const completionKey = getCompletionKey(options.slug);
  const hasCompleted = ref(localStorage.getItem(completionKey) === 'true');

  /**
   * Mark the guide as completed in localStorage.
   */
  function markCompleted(): void {
    localStorage.setItem(completionKey, 'true');
    hasCompleted.value = true;
  }

  /**
   * Reset the completion status for this guide.
   */
  function resetCompletion(): void {
    localStorage.removeItem(completionKey);
    hasCompleted.value = false;
  }

  /**
   * Transform API steps to Driver.js format, filtering out steps
   * with missing elements.
   */
  function transformSteps(
    apiSteps: InteractiveGuideStepResponse[]
  ): GuideStep[] {
    const validSteps: GuideStep[] = [];

    for (const step of apiSteps) {
      // Check if element exists in DOM
      const element = document.querySelector(step.element);

      if (!element) {
        console.warn(
          `[Interactive Guides] Step element not found: "${step.element}" — skipping step`
        );
        continue;
      }

      validSteps.push({
        element: step.element,
        popover: {
          title: step.title ?? undefined,
          description: step.description ?? undefined,
          side: step.side ?? undefined,
          align: step.align ?? undefined,
        },
      });
    }

    return validSteps;
  }

  /**
   * Create and configure the Driver.js instance.
   */
  function createDriver(steps: GuideStep[]): Driver {
    const config = {
      ...DRIVER_DEFAULTS,
      ...options.driverConfig,
      steps,
      onDestroyStarted: () => {
        // Check if tour was completed (on last step) or dismissed early
        const instance = driverInstance;
        if (instance?.isLastStep()) {
          markCompleted();
          options.onComplete?.();
        } else {
          options.onDismiss?.();
        }

        // Allow destruction to proceed
        instance?.destroy();
      },
      onDestroyed: () => {
        isActive.value = false;
        driverInstance = null;
      },
    };

    return driver(config);
  }

  /**
   * Start the tour manually.
   */
  async function startTour(): Promise<void> {
    // Don't start if already active
    if (isActive.value || driverInstance) {
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      // Fetch guide if not already loaded
      if (!guide.value) {
        guide.value = await store.fetchGuide(options.slug);
      }

      if (!guide.value) {
        throw new Error(`Guide not found: ${options.slug}`);
      }

      // Transform steps
      const steps = transformSteps(guide.value.steps);

      if (steps.length === 0) {
        console.warn(
          `[Interactive Guides] No valid steps found for guide: ${options.slug}`
        );
        return;
      }

      // Create and start driver
      driverInstance = createDriver(steps);
      isActive.value = true;
      options.onStart?.();
      driverInstance.drive();
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      error.value = e;
      options.onError?.(e);
      console.error(
        `[Interactive Guides] Error starting tour: ${options.slug}`,
        err
      );
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Start the tour at a specific step by element selector.
   */
  async function startTourAtStep(elementSelector: string): Promise<void> {
    if (isActive.value || driverInstance) return;

    isLoading.value = true;
    error.value = null;

    try {
      if (!guide.value) {
        guide.value = await store.fetchGuide(options.slug);
      }

      if (!guide.value) {
        throw new Error(`Guide not found: ${options.slug}`);
      }

      const steps = transformSteps(guide.value.steps);

      if (steps.length === 0) {
        console.warn(
          `[Interactive Guides] No valid steps found for guide: ${options.slug}`
        );
        return;
      }

      const stepIndex = steps.findIndex(
        (step) => step.element === elementSelector
      );

      if (stepIndex === -1) {
        console.warn(
          `[Interactive Guides] Step not found for element: ${elementSelector}`
        );
        return;
      }

      driverInstance = createDriver(steps);
      isActive.value = true;
      options.onStart?.();
      driverInstance.drive(stepIndex);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      error.value = e;
      options.onError?.(e);
      console.error(
        `[Interactive Guides] Error starting tour at step: ${options.slug}`,
        err
      );
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Stop and destroy the current tour.
   */
  function stopTour(): void {
    if (driverInstance) {
      driverInstance.destroy();
      driverInstance = null;
      isActive.value = false;
    }
  }

  function showBadge(elementSelector: string, element: Element): void {
    const state = hoverStates.get(elementSelector);
    if (!state || state.badge) return;

    const computedStyle = window.getComputedStyle(element as HTMLElement);
    if (computedStyle.position === 'static') {
      (element as HTMLElement).style.position = 'relative';
    }

    const badge = createBadgeElement(() => {
      // Hide badge before starting tour
      if (state.badge) {
        state.badge.remove();
        state.badge = null;
      }
      startTourAtStep(elementSelector);
    });

    element.appendChild(badge);
    state.badge = badge;
  }

  function hideBadge(elementSelector: string): void {
    const state = hoverStates.get(elementSelector);
    if (!state?.badge) return;

    state.badge.remove();
    state.badge = null;
  }

  function setupHoverListener(elementSelector: string): void {
    const element = document.querySelector(elementSelector);
    if (!element) return;

    const mouseenter = () => {
      const state = hoverStates.get(elementSelector);
      if (!state || state.badge) return;

      state.timer = setTimeout(() => {
        showBadge(elementSelector, element);
      }, hoverDelay);
    };

    const mouseleave = () => {
      const state = hoverStates.get(elementSelector);
      if (!state) return;

      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
      }

      // Hide badge when mouse leaves
      if (state.badge) {
        state.badge.remove();
        state.badge = null;
      }
    };

    hoverStates.set(elementSelector, {
      timer: null,
      badge: null,
      listeners: { mouseenter, mouseleave },
    });

    element.addEventListener('mouseenter', mouseenter);
    element.addEventListener('mouseleave', mouseleave);
  }

  /**
   * Initialise hover badges on step elements.
   */
  async function initHoverBadges(): Promise<void> {
    if (!options.showHoverBadges) return;

    try {
      if (!guide.value) {
        guide.value = await store.fetchGuide(options.slug);
      }

      if (!guide.value) return;

      for (const step of guide.value.steps) {
        if (!hoverStates.has(step.element)) {
          setupHoverListener(step.element);
        }
      }
    } catch (err) {
      console.error(
        `[Interactive Guides] Error initialising hover badges: ${options.slug}`,
        err
      );
    }
  }

  /**
   * Remove all hover badges and listeners.
   */
  function cleanupHoverBadges(): void {
    for (const [selector, state] of hoverStates) {
      if (state.timer) {
        clearTimeout(state.timer);
      }

      if (state.badge) {
        state.badge.remove();
      }

      const element = document.querySelector(selector);
      if (element) {
        element.removeEventListener('mouseenter', state.listeners.mouseenter);
        element.removeEventListener('mouseleave', state.listeners.mouseleave);
      }
    }

    hoverStates.clear();
  }

  // Auto-start handling
  if (options.autoStart && !hasCompleted.value) {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      startTour();
    }, 100);
  }

  // Auto-init hover badges
  if (options.showHoverBadges) {
    setTimeout(() => {
      initHoverBadges();
    }, 200);
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stopTour();
    cleanupHoverBadges();
  });

  return {
    startTour,
    stopTour,
    isLoading,
    isActive,
    hasCompleted,
    guide,
    error,
    resetCompletion,
    startTourAtStep,
    initHoverBadges,
    cleanupHoverBadges,
  };
}
